import { createSlice } from '@reduxjs/toolkit'
import dayjs from 'dayjs'

import { logworkApi } from '@core/app/api/logworkApi'
import { profileApi } from '@core/app/api/profileApi'
import { RootState } from '@core/app/store/store'
import {
    CalendarAvailability,
    CalendarHoliday,
    CalendarShift,
    CalendarTrip,
    CalendarVacation,
    Job,
    JobsSettings,
    LoggedWorkByIdData,
    LoggedWorkItem,
    SummaryDetail,
} from '@core/app/types/logworkApiType'
import {
    calculateAvailableJobsForTab,
    calculateFormattedJobsForTab,
    FormattedJobsByDay,
} from '@core/utils/helpers/jobCalculations'
import { calculateJobIdsForTab, JobIdsByDay, sortJobIdsKeys } from '@core/utils/helpers/jobIdsHelpers'

import { ExtendedLoggedWorkItem } from '@page/logwork/components/LoggedWork/LoggedTable'
import { DayjsType } from '@page/logwork/logwork'
import { Day, LogWorkTabType } from '@page/logwork/types/types'

type Summary = {
    breakQty: number
    grossQty: number
    netQty: number
    qty: number
    salary: {
        [key: string]: number
    }
    invoice: number
    details: SummaryDetail[]
}

export type WeekData = {
    hours: {
        [dayIndex: string]: Day | null
    }
    manualHours: {
        [dayIndex: string]: Day | null
    }
    piecework: {
        [dayIndex: string]: Day | null
    }
    addition: {
        [dayIndex: string]: Day | null
    }
}

interface LogworkState {
    jobs: Job[]
    jobsSettings: JobsSettings | null
    loggedWork: {
        [key: string]: LoggedWorkItem[]
    } | null
    choosedLoggedWork: {
        [key: string]: LoggedWorkItem[]
    } | null
    shifts: CalendarShift[]
    availabilities: CalendarAvailability[]
    holidays: CalendarHoliday[]
    trips: CalendarTrip[]
    vacation: CalendarVacation | null
    selectedJob: Job | null
    selectedJobs: {
        hours: {
            [dayIndex: string]: Job | null
        }
        manualHours: {
            [dayIndex: string]: Job | null
        }
        piecework: {
            [dayIndex: string]: Job | null
        }
        addition: {
            [dayIndex: string]: Job | null
        }
    }
    selectedWeekData: WeekData
    // Separate week schemas for each tab
    weekSchemaHours: { [dayIndex: string]: Day | null }
    weekSchemaManualHours: { [dayIndex: string]: Day | null }
    weekSchemaPiecework: { [dayIndex: string]: Day | null }
    weekSchemaAddition: { [dayIndex: string]: Day | null }
    loggedWorkChildProjects: {
        childProjectId: number
        name: string
    }[]
    loggedWorkJobs: {
        invoiceCurrencyId: number
        jobId: number
        name: string
        salaryCurrencyId: number
    }[]
    childProjects: {
        [key: string]: {
            childProjectId: number
            name: string
        }[]
    }
    resetTable: boolean
    isSidebarOpened: boolean
    isLogWorkHoursErrors: {
        [key: string]: {
            [key: string]: string[]
        }
    } | null
    summaryStats: Summary | null
    prevSummaryStats: Summary | null
    loggedWorkById: LoggedWorkByIdData | null
    isLoading: { mainData: boolean; loggedWorkById: boolean }
    isRegisterTrackerActive: boolean
    isDeleteTrackerActive: boolean
    isFileAttachmentError: boolean
    selectedDate: { start: DayjsType; end: DayjsType } | null
    workHourId: number
    loggedItemsToDelete: ExtendedLoggedWorkItem[]
    firstJobYear: number
    registerInfo: WeekData | null
    // Formatted jobs data separated by tab type
    formattedJobsHours: FormattedJobsByDay
    formattedJobsManualHours: FormattedJobsByDay
    formattedJobsPiecework: FormattedJobsByDay
    formattedJobsAddition: FormattedJobsByDay
    // Available jobs data separated by tab type
    availableJobsHours: FormattedJobsByDay
    availableJobsManualHours: FormattedJobsByDay
    availableJobsPiecework: FormattedJobsByDay
    availableJobsAddition: FormattedJobsByDay
    // Job IDs data separated by tab type
    jobIdsHours: JobIdsByDay
    jobIdsManualHours: JobIdsByDay
    jobIdsPiecework: JobIdsByDay
    jobIdsAddition: JobIdsByDay
    // Track manually deleted sub-days to prevent recreation from shifts
    deletedSubDays: {
        hours: { [key: string]: boolean }
        manualHours: { [key: string]: boolean }
        piecework: { [key: string]: boolean }
        addition: { [key: string]: boolean }
    }
    isSimplifiedUnitsView: boolean
    subscriptionId: number | null
    hasUserChanges: boolean
}

const initialState: LogworkState = {
    jobs: [],
    jobsSettings: null,
    loggedWork: null,
    choosedLoggedWork: null,
    shifts: [],
    availabilities: [],
    holidays: [],
    trips: [],
    vacation: null,
    selectedJob: null,
    selectedJobs: {
        manualHours: {},
        piecework: {},
        hours: {},
        addition: {},
    },
    selectedWeekData: {
        manualHours: {},
        piecework: {},
        hours: {},
        addition: {},
    },
    // Separate week schemas for each tab
    weekSchemaHours: {},
    weekSchemaManualHours: {},
    weekSchemaPiecework: {},
    weekSchemaAddition: {},
    resetTable: false,
    loggedWorkChildProjects: [],
    loggedWorkJobs: [],
    isSidebarOpened: true,
    isLogWorkHoursErrors: null,
    childProjects: {},
    summaryStats: null,
    prevSummaryStats: null,
    loggedWorkById: null,
    isLoading: { mainData: false, loggedWorkById: false },
    isRegisterTrackerActive: false,
    isDeleteTrackerActive: false,
    isFileAttachmentError: false,
    selectedDate: null,
    workHourId: 0,
    loggedItemsToDelete: [],
    firstJobYear: dayjs().year(),
    registerInfo: null,
    // Formatted jobs data separated by tab type
    formattedJobsHours: {},
    formattedJobsManualHours: {},
    formattedJobsPiecework: {},
    formattedJobsAddition: {},
    // Available jobs data separated by tab type
    availableJobsHours: {},
    availableJobsManualHours: {},
    availableJobsPiecework: {},
    availableJobsAddition: {},
    // Job IDs data separated by tab type
    jobIdsHours: {},
    jobIdsManualHours: {},
    jobIdsPiecework: {},
    jobIdsAddition: {},
    // Track manually deleted sub-days to prevent recreation from shifts
    deletedSubDays: {
        hours: {},
        manualHours: {},
        piecework: {},
        addition: {},
    },
    isSimplifiedUnitsView: false,
    subscriptionId: null,
    hasUserChanges: false,
}

const logworkSlice = createSlice({
    name: 'logwork',
    initialState,
    reducers: {
        setJobs(state, action) {
            state.jobs = action.payload
        },
        setLoggedWork(state, action) {
            state.loggedWork = action.payload
        },
        setChoosedLoggedWork(state, action) {
            state.choosedLoggedWork = action.payload
        },
        setSelectedJob(state, action) {
            state.selectedJob = action.payload
        },
        setSelectedJobs(state, action) {
            state.selectedJobs = action.payload
        },
        setSelectedWeekData(state, action) {
            state.selectedWeekData = action.payload
        },
        setWeekSchemaHours(state, action) {
            state.weekSchemaHours = action.payload
        },
        setWeekSchemaManualHours(state, action) {
            state.weekSchemaManualHours = action.payload
        },
        setWeekSchemaPiecework(state, action) {
            state.weekSchemaPiecework = action.payload
        },
        setWeekSchemaAddition(state, action) {
            state.weekSchemaAddition = action.payload
        },
        setResetTable(state, action) {
            state.resetTable = action.payload
        },
        setIsSidebarOpened(state, action) {
            state.isSidebarOpened = action.payload
        },
        handleLogWorkErrors(state, action) {
            state.isLogWorkHoursErrors = action.payload
        },
        setIsRegisterTrackerActive(state, action) {
            state.isRegisterTrackerActive = action.payload
        },
        setIsDeleteTrackerActive(state, action) {
            state.isDeleteTrackerActive = action.payload
        },
        setIsFileAttachmentError(state, action) {
            state.isFileAttachmentError = action.payload
        },
        setSelectedDate(state, action) {
            state.selectedDate = action.payload
        },
        setWorkHourId(state, action) {
            state.workHourId = action.payload
        },
        setLoggedItemsToDelete(state, action) {
            state.loggedItemsToDelete = action.payload
        },
        updateShift(state, action) {
            state.shifts = state.shifts.map((shift) => {
                if (shift.shiftId === action.payload.shiftId) {
                    return action.payload
                }
                return shift
            })
        },
        setIsLoading(state, action) {
            state.isLoading = action.payload
        },
        setRegisterInfo(state, action) {
            state.registerInfo = action.payload
        },
        setIsSimplifiedUnitsView(state, action) {
            state.isSimplifiedUnitsView = action.payload
        },
        setHasUserChanges(state, action) {
            state.hasUserChanges = action.payload
        },
        updateFormattedJobData(state, action) {
            const { dates, forceRecalculate = false } = action.payload
            if (!dates || !state.jobs.length) return

            // Convert serialized dates back to dayjs objects
            const dayjsDates = {
                start: dayjs(dates.start),
                end: dayjs(dates.end),
            }

            // Calculate data for each tab type separately
            const tabTypes: LogWorkTabType[] = ['hours', 'manualHours', 'piecework', 'addition']

            tabTypes.forEach((tabType) => {
                const formattedJobs = calculateFormattedJobsForTab(state.jobs, state.shifts, dayjsDates, tabType)
                const availableJobs = calculateAvailableJobsForTab(formattedJobs)
                const calculatedJobIds = calculateJobIdsForTab(
                    state.jobs,
                    state.shifts,
                    dayjsDates,
                    formattedJobs,
                    availableJobs,
                    tabType
                )

                // Helper function to preserve user-added sub-day jobIds and main day jobIds
                const mergeJobIds = (
                    calculated: JobIdsByDay,
                    existing: JobIdsByDay,
                    forceRecalc: boolean
                ): JobIdsByDay => {
                    // If forceRecalculate is true, use only calculated values (for date changes)
                    if (forceRecalc) {
                        return sortJobIdsKeys(calculated)
                    }

                    const merged = { ...calculated }

                    // Find all keys that exist in the old state
                    const recalculatedKeys = new Set(Object.keys(calculated))

                    Object.keys(existing).forEach((key) => {
                        // Preserve user-set values for main days (0-6)
                        // Only overwrite if existing value is 0 or calculated value is different and also non-zero
                        if (/^\d+$/.test(key) && parseInt(key, 10) >= 0 && parseInt(key, 10) <= 6) {
                            const existingValue = existing[key]
                            const calculatedValue = calculated[key]

                            // If existing has a non-zero jobId, preserve it
                            // Only use calculated value if existing is 0
                            if (existingValue !== 0) {
                                merged[key] = existingValue
                            } else if (calculatedValue !== 0) {
                                merged[key] = calculatedValue
                            }
                        } else {
                            // Preserve all user-added sub-days that weren't recalculated
                            if (key.includes('-') && !recalculatedKeys.has(key)) {
                                merged[key] = existing[key]
                            }
                        }
                    })

                    return sortJobIdsKeys(merged)
                }

                // Get existing jobIds for this tab
                let existingJobIds: JobIdsByDay = {}
                switch (tabType) {
                    case 'hours':
                        existingJobIds = state.jobIdsHours
                        break
                    case 'manualHours':
                        existingJobIds = state.jobIdsManualHours
                        break
                    case 'piecework':
                        existingJobIds = state.jobIdsPiecework
                        break
                    case 'addition':
                        existingJobIds = state.jobIdsAddition
                        break
                }

                // Merge calculated jobIds with existing user-added sub-days
                const mergedJobIds = mergeJobIds(calculatedJobIds, existingJobIds, forceRecalculate)

                // Update the appropriate field based on tab type
                switch (tabType) {
                    case 'hours':
                        state.formattedJobsHours = formattedJobs
                        state.availableJobsHours = availableJobs
                        state.jobIdsHours = mergedJobIds
                        break
                    case 'manualHours':
                        state.formattedJobsManualHours = formattedJobs
                        state.availableJobsManualHours = availableJobs
                        state.jobIdsManualHours = mergedJobIds
                        break
                    case 'piecework':
                        state.formattedJobsPiecework = formattedJobs
                        state.availableJobsPiecework = availableJobs
                        state.jobIdsPiecework = mergedJobIds
                        break
                    case 'addition':
                        state.formattedJobsAddition = formattedJobs
                        state.availableJobsAddition = availableJobs
                        state.jobIdsAddition = mergedJobIds
                        break
                }
            })
        },
        addJobIdForSubDay(state, action) {
            const { tabType, dayIndex, jobId } = action.payload

            // Helper function to add sub-day jobId with proper key format
            const addSubDayJobId = (jobIds: JobIdsByDay, mainDayIndex: number, newJobId: number) => {
                const newJobIds = { ...jobIds }

                // Find the next available sub-day key for this main day
                let subDayNumber = 1
                let subDayKey = `${mainDayIndex}-${subDayNumber}`

                // Find the highest sub-day number for this main day
                const existingSubDays = Object.keys(newJobIds)
                    .filter((key) => key.startsWith(`${mainDayIndex}-`))
                    .map((key) => parseInt(key.split('-')[1], 10))
                    .filter((num) => !isNaN(num))

                if (existingSubDays.length > 0) {
                    subDayNumber = Math.max(...existingSubDays) + 1
                    subDayKey = `${mainDayIndex}-${subDayNumber}`
                }

                // Add the new sub-day jobId
                newJobIds[subDayKey] = newJobId
                return sortJobIdsKeys(newJobIds)
            }

            // Add job ID for a sub-day (additional row)
            switch (tabType) {
                case 'hours':
                    state.jobIdsHours = addSubDayJobId(state.jobIdsHours, dayIndex, jobId)
                    break
                case 'manualHours':
                    state.jobIdsManualHours = addSubDayJobId(state.jobIdsManualHours, dayIndex, jobId)
                    break
                case 'piecework':
                    state.jobIdsPiecework = addSubDayJobId(state.jobIdsPiecework, dayIndex, jobId)
                    break
                case 'addition':
                    state.jobIdsAddition = addSubDayJobId(state.jobIdsAddition, dayIndex, jobId)
                    break
            }
        },
        removeJobIdForSubDay(state, action) {
            const { tabType, dayIndex } = action.payload

            // Helper function to remove sub-day jobId
            const removeSubDayJobId = (jobIds: JobIdsByDay, subDayKey: string) => {
                const newJobIds = { ...jobIds }

                // Check if it's a main day (0-6) - these cannot be removed
                if (/^\d+$/.test(subDayKey) && parseInt(subDayKey, 10) >= 0 && parseInt(subDayKey, 10) <= 6) {
                    return newJobIds
                }

                // Check if it's a sub-day key (format: "mainDay-subDay")
                if (!/^\d+-\d+$/.test(subDayKey)) {
                    return newJobIds
                }

                // Remove the sub-day jobId
                delete newJobIds[subDayKey]

                return sortJobIdsKeys(newJobIds)
            }

            // Add to deleted sub-days tracking
            state.deletedSubDays[tabType as keyof typeof state.deletedSubDays][dayIndex] = true

            // Remove job ID for a sub-day
            switch (tabType) {
                case 'hours':
                    state.jobIdsHours = removeSubDayJobId(state.jobIdsHours, dayIndex)
                    break
                case 'manualHours':
                    state.jobIdsManualHours = removeSubDayJobId(state.jobIdsManualHours, dayIndex)
                    break
                case 'piecework':
                    state.jobIdsPiecework = removeSubDayJobId(state.jobIdsPiecework, dayIndex)
                    break
                case 'addition':
                    state.jobIdsAddition = removeSubDayJobId(state.jobIdsAddition, dayIndex)
                    break
            }
        },
        clearJobIdsSubDays(state, action) {
            const { tabType } = action.payload

            // Helper function to clear all sub-days from jobIds and reset main days to 0
            // This will be called after successful registration to remove all sub-days (both shift-based and user-added)
            // and return table to its initial state with only main days (0-6) with no selected jobs
            const clearSubDaysFromJobIds = (jobIds: JobIdsByDay) => {
                const newJobIds: JobIdsByDay = {}

                // Reset all main days (0-6) to 0 (no job selected), remove all sub-days (keys containing a dash)
                for (let i = 0; i <= 6; i++) {
                    newJobIds[i.toString()] = 0
                }

                return newJobIds
            }

            // Helper function to clear all sub-days from week schema
            const clearSubDaysFromWeekSchema = (weekSchema: { [dayId: string]: any }) => {
                const newWeekSchema: { [dayId: string]: any } = {}

                // Keep only main days (0-6) - objects with id that don't contain a dash
                Object.keys(weekSchema).forEach((key) => {
                    const dayData = weekSchema[key]
                    if (dayData && dayData.id && !dayData.id.includes('-')) {
                        newWeekSchema[key] = dayData
                    }
                })

                return newWeekSchema
            }

            // Clear all sub-days from jobIds and week schema for the specified tab
            // Also clear selectedJobs for the tab to reset job selections
            switch (tabType) {
                case 'hours':
                    state.jobIdsHours = clearSubDaysFromJobIds(state.jobIdsHours)
                    state.weekSchemaHours = clearSubDaysFromWeekSchema(state.weekSchemaHours)
                    state.selectedJobs.hours = {}
                    break
                case 'manualHours':
                    state.jobIdsManualHours = clearSubDaysFromJobIds(state.jobIdsManualHours)
                    state.weekSchemaManualHours = clearSubDaysFromWeekSchema(state.weekSchemaManualHours)
                    state.selectedJobs.manualHours = {}
                    break
                case 'piecework':
                    state.jobIdsPiecework = clearSubDaysFromJobIds(state.jobIdsPiecework)
                    state.weekSchemaPiecework = clearSubDaysFromWeekSchema(state.weekSchemaPiecework)
                    state.selectedJobs.piecework = {}
                    break
                case 'addition':
                    state.jobIdsAddition = clearSubDaysFromJobIds(state.jobIdsAddition)
                    state.weekSchemaAddition = clearSubDaysFromWeekSchema(state.weekSchemaAddition)
                    state.selectedJobs.addition = {}
                    break
            }
        },
        clearDeletedSubDays(state, action) {
            const { tabType } = action.payload

            // Clear deleted sub-days tracking for the specified tab
            if (tabType) {
                state.deletedSubDays[tabType as keyof typeof state.deletedSubDays] = {}
            } else {
                // Clear all tabs if no specific tab provided
                Object.keys(state.deletedSubDays).forEach((tab) => {
                    state.deletedSubDays[tab as keyof typeof state.deletedSubDays] = {}
                })
            }
        },
        updateJobIdForDay(state, action) {
            const { tabType, dayIndex, jobId } = action.payload

            // Helper function to update jobId for a day
            const updateJobId = (jobIds: JobIdsByDay, dayKey: string, newJobId: number) => {
                const newJobIds = { ...jobIds }
                newJobIds[dayKey] = newJobId
                return newJobIds
            }

            // Update jobId for the specific day and tab
            switch (tabType) {
                case 'hours':
                    state.jobIdsHours = updateJobId(state.jobIdsHours, dayIndex, jobId)
                    break
                case 'manualHours':
                    state.jobIdsManualHours = updateJobId(state.jobIdsManualHours, dayIndex, jobId)
                    break
                case 'piecework':
                    state.jobIdsPiecework = updateJobId(state.jobIdsPiecework, dayIndex, jobId)
                    break
                case 'addition':
                    state.jobIdsAddition = updateJobId(state.jobIdsAddition, dayIndex, jobId)
                    break
            }
        },
        removeProcessedShifts(state, action) {
            const { processedJobCalendarIds, tabType } = action.payload

            // Get shifts to find jobId from jobCalendarId (before removing them)
            const originalShifts = [...state.shifts]
            const processedShifts = processedJobCalendarIds
                .map((calendarId: number) => {
                    const shift = originalShifts.find((s) => s.jobCalendarId === calendarId)
                    return shift ? { jobCalendarId: calendarId, jobId: shift.jobId } : null
                })
                .filter(
                    (
                        shift: { jobCalendarId: number; jobId: number } | null
                    ): shift is { jobCalendarId: number; jobId: number } => shift !== null
                )

            // Helper function to remove processed shifts from jobIds
            const removeProcessedFromJobIds = (
                jobIds: JobIdsByDay,
                processedShifts: { jobCalendarId: number; jobId: number }[]
            ) => {
                const newJobIds = { ...jobIds }

                // First, remove sub-days that correspond to processed shifts
                const processedJobCalendarIdsSet = new Set(processedJobCalendarIds)

                Object.keys(newJobIds).forEach((key) => {
                    if (key.includes('-')) {
                        // This is a sub-day, check if it corresponds to a processed shift
                        const [mainDayIndex, subDayIndex] = key.split('-').map(Number)

                        // Find the processed shift to get the correct date
                        const processedShift = originalShifts.find((s) =>
                            processedJobCalendarIds.includes(s.jobCalendarId)
                        )

                        if (!processedShift) {
                            return
                        }

                        // Use the date from the processed shift to find all shifts for that day
                        const mainDayDate = dayjs(processedShift.date)
                        const shiftsForMainDay = originalShifts.filter((s) => dayjs(s.date).isSame(mainDayDate, 'day'))

                        // Check if the shift at this sub-day index was processed
                        const shiftAtIndex = shiftsForMainDay[subDayIndex - 1]
                        if (shiftAtIndex && processedJobCalendarIdsSet.has(shiftAtIndex.jobCalendarId)) {
                            delete newJobIds[key]
                        } else {
                        }
                    }
                })

                // Then, remove main days if no remaining shifts with the same jobId
                Object.keys(newJobIds).forEach((key) => {
                    if (!key.includes('-')) {
                        const jobId = newJobIds[key]
                        const processedJobIds = processedShifts.map((s) => s.jobId)

                        if (processedJobIds.includes(jobId)) {
                            // Check if there are any remaining shifts with this jobId that are not being processed
                            const remainingShiftsWithSameJobId = originalShifts.filter(
                                (shift) =>
                                    shift.jobId === jobId && !processedJobCalendarIds.includes(shift.jobCalendarId)
                            )

                            // Only remove if no remaining shifts with the same jobId
                            if (remainingShiftsWithSameJobId.length === 0) {
                                delete newJobIds[key]
                            }
                        }
                    }
                })

                return newJobIds
            }

            // Helper function to reorder day IDs when parent day is removed
            const reorderDayIds = (weekSchema: { [dayId: string]: any }) => {
                const newWeekSchema = { ...weekSchema }
                const dayKeys = Object.keys(newWeekSchema).sort((a, b) => {
                    // Sort by day index first, then by sub-day index
                    const aParts = a.split('-')
                    const bParts = b.split('-')
                    const aDayIndex = parseInt(aParts[0], 10)
                    const bDayIndex = parseInt(bParts[0], 10)

                    if (aDayIndex !== bDayIndex) {
                        return aDayIndex - bDayIndex
                    }

                    // If same day, sort by sub-day index
                    const aSubIndex = aParts[1] ? parseInt(aParts[1], 10) : 0
                    const bSubIndex = bParts[1] ? parseInt(bParts[1], 10) : 0
                    return aSubIndex - bSubIndex
                })

                // Group days by their main day index
                const daysByMainIndex: { [mainIndex: string]: { [subIndex: string]: any } } = {}
                dayKeys.forEach((key) => {
                    const parts = key.split('-')
                    const mainIndex = parts[0]
                    const subIndex = parts[1] || '0' // Main day is sub-index 0

                    if (!daysByMainIndex[mainIndex]) {
                        daysByMainIndex[mainIndex] = {}
                    }
                    daysByMainIndex[mainIndex][subIndex] = newWeekSchema[key]
                })

                // Reorder days: if main day is missing, shift sub-days up
                const reorderedSchema: { [dayId: string]: any } = {}
                let currentMainIndex = 0

                Object.keys(daysByMainIndex).forEach((originalMainIndex) => {
                    const mainIndex = parseInt(originalMainIndex, 10)
                    const subDays = daysByMainIndex[originalMainIndex]

                    // Check if main day exists (sub-index 0)
                    if (subDays['0']) {
                        // Main day exists, keep it and all sub-days as is
                        Object.keys(subDays).forEach((subIndex) => {
                            const originalKey =
                                subIndex === '0' ? originalMainIndex : `${originalMainIndex}-${subIndex}`
                            reorderedSchema[originalKey] = {
                                ...subDays[subIndex],
                                id: originalKey,
                                dayId: originalMainIndex,
                            }
                        })
                    } else {
                        // Main day is missing, shift first sub-day to main day position
                        const subDayKeys = Object.keys(subDays)
                            .filter((key) => key !== '0')
                            .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))

                        if (subDayKeys.length > 0) {
                            // Move first sub-day to main day position
                            const firstSubDay = subDayKeys[0]
                            const newMainKey = currentMainIndex.toString()
                            reorderedSchema[newMainKey] = {
                                ...subDays[firstSubDay],
                                id: newMainKey,
                                dayId: newMainKey,
                                isSubDay: false,
                            }

                            // Move remaining sub-days up by one level
                            subDayKeys.slice(1).forEach((subIndex, index) => {
                                const newSubKey = `${currentMainIndex}-${index + 1}`
                                reorderedSchema[newSubKey] = {
                                    ...subDays[subIndex],
                                    id: newSubKey,
                                    dayId: currentMainIndex.toString(),
                                }
                            })
                        }
                    }
                    currentMainIndex++
                })

                return reorderedSchema
            }

            // Helper function to remove processed shifts from week schema
            const removeProcessedFromWeekSchema = (
                weekSchema: { [dayId: string]: any },
                processedJobCalendarIds: number[]
            ) => {
                const newWeekSchema = { ...weekSchema }

                // First, remove main days that match processed jobCalendarIds
                Object.keys(newWeekSchema).forEach((key) => {
                    if (!key.includes('-')) {
                        const dayData = newWeekSchema[key]
                        if (
                            dayData &&
                            dayData.jobCalendarId &&
                            processedJobCalendarIds.includes(dayData.jobCalendarId)
                        ) {
                            delete newWeekSchema[key]
                        }
                    }
                })

                // Then, remove sub-days that correspond to processed shifts
                const processedJobCalendarIdsSet = new Set(processedJobCalendarIds)
                Object.keys(newWeekSchema).forEach((key) => {
                    if (key.includes('-')) {
                        const [mainDayIndex, subDayIndex] = key.split('-').map(Number)

                        // Find the processed shift to get the correct date
                        const processedShift = originalShifts.find((s) =>
                            processedJobCalendarIds.includes(s.jobCalendarId)
                        )

                        if (!processedShift) {
                            return
                        }

                        // Use the date from the processed shift to find all shifts for that day
                        const mainDayDate = dayjs(processedShift.date)
                        const shiftsForMainDay = originalShifts.filter((s) => dayjs(s.date).isSame(mainDayDate, 'day'))

                        // Check if the shift at this sub-day index was processed
                        const shiftAtIndex = shiftsForMainDay[subDayIndex - 1]
                        if (shiftAtIndex && processedJobCalendarIdsSet.has(shiftAtIndex.jobCalendarId)) {
                            delete newWeekSchema[key]
                        } else {
                        }
                    }
                })

                // Reorder days after removal
                return reorderDayIds(newWeekSchema)
            }

            // Remove processed shifts from shifts array
            state.shifts = state.shifts.filter((shift) => !processedJobCalendarIds.includes(shift.jobCalendarId))

            // Remove processed shifts from jobIds and week schema for the specified tab
            switch (tabType) {
                case 'hours':
                    state.jobIdsHours = removeProcessedFromJobIds(state.jobIdsHours, processedShifts)
                    state.weekSchemaHours = removeProcessedFromWeekSchema(
                        state.weekSchemaHours,
                        processedJobCalendarIds
                    )
                    break
                case 'manualHours':
                    state.jobIdsManualHours = removeProcessedFromJobIds(state.jobIdsManualHours, processedShifts)
                    state.weekSchemaManualHours = removeProcessedFromWeekSchema(
                        state.weekSchemaManualHours,
                        processedJobCalendarIds
                    )
                    break
                case 'piecework':
                    state.jobIdsPiecework = removeProcessedFromJobIds(state.jobIdsPiecework, processedShifts)
                    state.weekSchemaPiecework = removeProcessedFromWeekSchema(
                        state.weekSchemaPiecework,
                        processedJobCalendarIds
                    )
                    break
                case 'addition':
                    state.jobIdsAddition = removeProcessedFromJobIds(state.jobIdsAddition, processedShifts)
                    state.weekSchemaAddition = removeProcessedFromWeekSchema(
                        state.weekSchemaAddition,
                        processedJobCalendarIds
                    )
                    break
            }
        },
    },
    extraReducers(builder) {
        builder.addMatcher(logworkApi.endpoints.getLoggedWorkById.matchPending, (state) => {
            state.isLoading.loggedWorkById = true
        })
        builder.addMatcher(logworkApi.endpoints.getLoggedWorkById.matchRejected, (state) => {
            state.isLoading.loggedWorkById = false
        })
        builder.addMatcher(logworkApi.endpoints.getLoggedWorkById.matchFulfilled, (state, action) => {
            state.loggedWorkById = action.payload
            state.isLoading.loggedWorkById = false
        })
        builder.addMatcher(logworkApi.endpoints.getData.matchPending, (state) => {
            state.isLoading.mainData = true
        })
        builder.addMatcher(logworkApi.endpoints.getData.matchRejected, (state) => {
            state.isLoading.mainData = false
        })
        builder.addMatcher(logworkApi.endpoints.getData.matchFulfilled, (state, action) => {
            action.payload.jobs.forEach((job: Job) => {
                if (job.childProjects.length > 0) {
                    Object.assign(state.childProjects, {
                        [job.jobId]: [...job.childProjects],
                    })
                }
            })

            state.jobs = action.payload.jobs
            state.jobsSettings = action.payload.employeeSettings
            state.firstJobYear = action.payload.firstJobYear
            state.loggedWork = action.payload.work.loggedWork
            state.summaryStats = action.payload.work.summary
            state.prevSummaryStats = action.payload.work.prePeriodSummary
            state.loggedWorkChildProjects = action.payload.work.childProjects
            state.loggedWorkJobs = action.payload.work.jobs
            state.selectedJobs = {
                manualHours: {},
                piecework: {},
                hours: {},
                addition: {},
            }

            if (action.payload.shifts) {
                const logged0 = action.payload.shifts.filter((shift: CalendarShift) => {
                    if (shift.logged !== 0) return false

                    // Find the job for this shift
                    const job = state.jobs.find((job: Job) => job.jobId === shift.jobId)
                    if (!job || !job.settings.closingDate) return true

                    // Check if shift date is before closing date
                    const shiftDate = dayjs(shift.date).startOf('day')
                    const closingDate = dayjs(job.settings.closingDate).startOf('day')

                    return !shiftDate.isBefore(closingDate)
                })
                state.shifts = logged0
            }
            if (action.payload.availability) {
                state.availabilities = action.payload.availability
            }
            if (action.payload.nationalHolidays) {
                state.holidays = action.payload.nationalHolidays
            }
            if (action.payload.trips) {
                state.trips = action.payload.trips
            }
            if (action.payload.vacation) {
                state.vacation = action.payload.vacation
            }

            // Calculate formatted job data if we have selected dates
            if (state.selectedDate) {
                const tabTypes: LogWorkTabType[] = ['hours', 'manualHours', 'piecework', 'addition']
                const selectedDate = state.selectedDate

                tabTypes.forEach((tabType) => {
                    const formattedJobs = calculateFormattedJobsForTab(state.jobs, state.shifts, selectedDate, tabType)
                    const availableJobs = calculateAvailableJobsForTab(formattedJobs)
                    const jobIds = calculateJobIdsForTab(
                        state.jobs,
                        state.shifts,
                        selectedDate,
                        formattedJobs,
                        availableJobs,
                        tabType
                    )

                    // Update the appropriate field based on tab type
                    switch (tabType) {
                        case 'hours':
                            state.formattedJobsHours = formattedJobs
                            state.availableJobsHours = availableJobs
                            state.jobIdsHours = jobIds
                            break
                        case 'manualHours':
                            state.formattedJobsManualHours = formattedJobs
                            state.availableJobsManualHours = availableJobs
                            state.jobIdsManualHours = jobIds
                            break
                        case 'piecework':
                            state.formattedJobsPiecework = formattedJobs
                            state.availableJobsPiecework = availableJobs
                            state.jobIdsPiecework = jobIds
                            break
                        case 'addition':
                            state.formattedJobsAddition = formattedJobs
                            state.availableJobsAddition = availableJobs
                            state.jobIdsAddition = jobIds
                            break
                    }
                })
            }

            state.isLoading.mainData = false
        })
        builder.addMatcher(profileApi.endpoints.getHeaderInfo.matchFulfilled, (state, action) => {
            state.subscriptionId = action.payload.subscriptionId
        })
    },
})

export default logworkSlice.reducer
export const {
    setJobs,
    setLoggedWork,
    setSelectedJobs,
    setSelectedJob,
    setSelectedWeekData,
    setWeekSchemaHours,
    setWeekSchemaManualHours,
    setWeekSchemaPiecework,
    setWeekSchemaAddition,
    setResetTable,
    setIsSidebarOpened,
    handleLogWorkErrors,
    setChoosedLoggedWork,
    setIsRegisterTrackerActive,
    setIsDeleteTrackerActive,
    setIsFileAttachmentError,
    setSelectedDate,
    setWorkHourId,
    setLoggedItemsToDelete,
    updateShift,
    setIsLoading,
    setRegisterInfo,
    updateFormattedJobData,
    addJobIdForSubDay,
    removeJobIdForSubDay,
    clearJobIdsSubDays,
    clearDeletedSubDays,
    removeProcessedShifts,
    setIsSimplifiedUnitsView,
    updateJobIdForDay,
    setHasUserChanges,
} = logworkSlice.actions
export const storeIsSimplifiedUnitsView = (state: RootState) => state.logwork.isSimplifiedUnitsView
export const storeSubscriptionId = (state: RootState) => state.logwork.subscriptionId
export const storeJobs = (state: RootState) => state.logwork.jobs
export const storeLoggedWork = (state: RootState) => state.logwork.loggedWork
export const storeSelectedJob = (state: RootState) => state.logwork.selectedJob
export const storeSelectedJobs = (state: RootState) => state.logwork.selectedJobs
export const storeResetTable = (state: RootState) => state.logwork.resetTable
export const storeSelectedWeekData = (state: RootState) => state.logwork.selectedWeekData
export const storeLoggedWorkChildProjects = (state: RootState) => state.logwork.loggedWorkChildProjects
export const storeShifts = (state: RootState) => state.logwork.shifts
export const storeIsSidebarOpened = (state: RootState) => state.logwork.isSidebarOpened
export const storeIsLogWorkHoursErrors = (state: RootState) => state.logwork.isLogWorkHoursErrors
export const storeChildProjects = (state: RootState) => state.logwork.childProjects
export const storeSummaryStats = (state: RootState) => state.logwork.summaryStats
export const storePrevSummaryStats = (state: RootState) => state.logwork.prevSummaryStats
export const storeAvailabilities = (state: RootState) => state.logwork.availabilities
export const storeHolidays = (state: RootState) => state.logwork.holidays
export const storeTrips = (state: RootState) => state.logwork.trips
export const storeVacation = (state: RootState) => state.logwork.vacation
export const storeChoosedLoggedWork = (state: RootState) => state.logwork.choosedLoggedWork
export const storeIsLoading = (state: RootState) => state.logwork.isLoading
export const storeLoggedWorkById = (state: RootState) => state.logwork.loggedWorkById
export const storeIsRegisterTrackerActive = (state: RootState) => state.logwork.isRegisterTrackerActive
export const storeIsDeleteTrackerActive = (state: RootState) => state.logwork.isDeleteTrackerActive
export const storeLoggedWorkJobs = (state: RootState) => state.logwork.loggedWorkJobs
export const storeJobsSettings = (state: RootState) => state.logwork.jobsSettings
export const storeIsFileAttachmentError = (state: RootState) => state.logwork.isFileAttachmentError
export const storeSelectedDate = (state: RootState) => state.logwork.selectedDate
export const storeWorkHourId = (state: RootState) => state.logwork.workHourId
export const storeLoggedItemsToDelete = (state: RootState) => state.logwork.loggedItemsToDelete
export const storeFirstJobYear = (state: RootState) => state.logwork.firstJobYear
export const storeRegisterInfo = (state: RootState) => state.logwork.registerInfo

// Formatted jobs selectors
export const storeFormattedJobsHours = (state: RootState) => state.logwork.formattedJobsHours
export const storeFormattedJobsManualHours = (state: RootState) => state.logwork.formattedJobsManualHours
export const storeFormattedJobsPiecework = (state: RootState) => state.logwork.formattedJobsPiecework
export const storeFormattedJobsAddition = (state: RootState) => state.logwork.formattedJobsAddition

// Available jobs selectors
export const storeAvailableJobsHours = (state: RootState) => state.logwork.availableJobsHours
export const storeAvailableJobsManualHours = (state: RootState) => state.logwork.availableJobsManualHours
export const storeAvailableJobsPiecework = (state: RootState) => state.logwork.availableJobsPiecework
export const storeAvailableJobsAddition = (state: RootState) => state.logwork.availableJobsAddition

// Job IDs selectors
export const storeJobIdsHours = (state: RootState) => state.logwork.jobIdsHours || {}
export const storeJobIdsManualHours = (state: RootState) => state.logwork.jobIdsManualHours || {}
export const storeJobIdsPiecework = (state: RootState) => state.logwork.jobIdsPiecework || {}
export const storeJobIdsAddition = (state: RootState) => state.logwork.jobIdsAddition || {}

// Week schema selectors
export const storeWeekSchemaHours = (state: RootState) => state.logwork.weekSchemaHours
export const storeWeekSchemaManualHours = (state: RootState) => state.logwork.weekSchemaManualHours
export const storeWeekSchemaPiecework = (state: RootState) => state.logwork.weekSchemaPiecework
export const storeWeekSchemaAddition = (state: RootState) => state.logwork.weekSchemaAddition

// Deleted sub-days selector
export const storeDeletedSubDays = (state: RootState) => state.logwork.deletedSubDays

export const storeHasUserChanges = (state: RootState) => state.logwork.hasUserChanges
